/* eslint-disable @typescript-eslint/no-unused-vars -- plain ES5 for the theme: catch needs a binding */
/*
 * Urban Customz — first-touch attribution for urbancustomz.com.
 *
 * Deployed as the theme asset `assets/uc-attribution.js` (loaded in the
 * <head> of every page) — this file IS that asset, so the tests run the
 * code the site runs. Plain ES5, no dependencies.
 *
 * What it does:
 *   - On the first page of a visit with no first touch on record, keeps
 *     ONE first touch: that page, its title, the external referrer, UTM
 *     tags and ad click ids already in the URL, and the time. Later pages
 *     never overwrite it. It expires after 90 days.
 *   - Keeps the latest campaign touch of the current session (a later
 *     click from an ad or a newsletter) separately.
 *   - ucAttribution.payload() hands both, plus the current page as the
 *     conversion touch, to the quote form.
 *
 * Privacy: first-party storage on this site only, no cookies, no
 * fingerprinting, nothing sent anywhere except with the quote form the
 * visitor submits. URLs are stored without their query string except
 * UTM tags and ad click ids. Referrers are stored as origin + path. If
 * Shopify's Customer Privacy API says analytics are not allowed, nothing
 * is stored and anything stored is removed.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    root.ucAttribution = api.install(root);
  }
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  var VERSION = 1;
  var FIRST_KEY = "uc_attr_first_v1";
  var LATEST_KEY = "uc_attr_latest_v1";
  var WINDOW_DAYS = 90;
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  var CLICK_KEYS = ["gclid", "gbraid", "wbraid", "gad_source", "fbclid", "msclkid", "ttclid", "li_fat_id", "twclid", "epik", "sccid"];
  var MAX = 300;

  function clip(value, max) {
    if (typeof value !== "string") return null;
    var v = value.replace(/[\u0000-\u001f\u007f]/g, "").trim();
    if (!v) return null;
    return v.length > (max || MAX) ? v.slice(0, max || MAX) : v;
  }

  function parseUrl(href) {
    try {
      return new URL(href);
    } catch (_e) {
      return null;
    }
  }

  /** URL with only UTM tags and click ids kept in the query. */
  function cleanUrl(u) {
    var kept = [];
    UTM_KEYS.concat(CLICK_KEYS).forEach(function (k) {
      var v = u.searchParams.get(k);
      if (v) kept.push(encodeURIComponent(k) + "=" + encodeURIComponent(clip(v, 200)));
    });
    return clip(u.origin + u.pathname + (kept.length ? "?" + kept.join("&") : ""), 600);
  }

  function tagsOf(u) {
    var utm = {};
    var clicks = {};
    UTM_KEYS.forEach(function (k) {
      var v = clip(u.searchParams.get(k), 150);
      if (v) utm[k.slice(4)] = v;
    });
    CLICK_KEYS.forEach(function (k) {
      var v = clip(u.searchParams.get(k), 200);
      if (v) clicks[k] = v;
    });
    return { utm: utm, clicks: clicks };
  }

  function isEmpty(o) {
    for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) return false;
    return true;
  }

  /** origin + path of an external referrer; null for none or our own pages. */
  function externalReferrer(referrer, ownHost) {
    var r = referrer ? parseUrl(referrer) : null;
    if (!r || (r.protocol !== "http:" && r.protocol !== "https:")) return null;
    var host = r.hostname.replace(/^www\./, "");
    if (host === ownHost.replace(/^www\./, "")) return null;
    return { url: clip(r.origin + r.pathname, MAX), domain: clip(host, 200) };
  }

  function read(storage, key) {
    try {
      var raw = storage && storage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  }

  function write(storage, key, value) {
    try {
      if (storage) storage.setItem(key, JSON.stringify(value));
    } catch (_e) {
      /* storage full or blocked: attribution is optional */
    }
  }

  function remove(storage, key) {
    try {
      if (storage) storage.removeItem(key);
    } catch (_e) {
      /* nothing to do */
    }
  }

  /**
   * The tracker, with every browser dependency passed in so it can be
   * tested without a browser.
   *   env.location  { href, hostname }
   *   env.document  { title, referrer }
   *   env.local / env.session  Storage-like (getItem/setItem/removeItem)
   *   env.now()     ms since epoch
   *   env.consent() true | false | undefined (undefined = not known yet)
   */
  function createTracker(env) {
    function allowed() {
      return env.consent() !== false;
    }

    /** Run on every page view. Returns what is now on record. */
    function track() {
      if (!allowed()) {
        remove(env.local, FIRST_KEY);
        remove(env.session, LATEST_KEY);
        return { first: null, latest: null };
      }
      var u = parseUrl(env.location.href);
      if (!u) return { first: read(env.local, FIRST_KEY), latest: read(env.session, LATEST_KEY) };

      var now = env.now();
      var tags = tagsOf(u);
      var ref = externalReferrer(env.document.referrer, env.location.hostname);
      var first = read(env.local, FIRST_KEY);
      if (first && (first.v !== VERSION || !first.at || now - Date.parse(first.at) > WINDOW_DAYS * 86400000)) {
        first = null;
      }

      if (!first) {
        first = {
          v: VERSION,
          at: new Date(now).toISOString(),
          url: cleanUrl(u),
          path: clip(u.pathname, MAX),
          title: clip(env.document.title, 200),
          referrer: ref ? ref.url : null,
          referring_domain: ref ? ref.domain : null,
          utm: tags.utm,
          click_ids: tags.clicks,
          // A same-site referrer on a "first" page means storage was lost
          // mid-visit: this is not really the landing page.
          internal_referrer: !ref && !!env.document.referrer
        };
        write(env.local, FIRST_KEY, first);
      } else if (!first.title && first.path === u.pathname && env.document.title) {
        first.title = clip(env.document.title, 200);
        write(env.local, FIRST_KEY, first);
      }

      // A new campaign arrival in this session: UTM tags, a click id, or
      // an external referrer. Ordinary page-to-page navigation leaves it.
      var latest = read(env.session, LATEST_KEY);
      if (!isEmpty(tags.utm) || !isEmpty(tags.clicks) || ref) {
        latest = {
          v: VERSION,
          at: new Date(now).toISOString(),
          url: cleanUrl(u),
          referring_domain: ref ? ref.domain : null,
          utm: tags.utm,
          click_ids: tags.clicks
        };
        write(env.session, LATEST_KEY, latest);
      }
      return { first: first, latest: latest };
    }

    /** For the quote form: first touch, latest touch, and this page as the conversion. */
    function payload() {
      var state = allowed() ? { first: read(env.local, FIRST_KEY), latest: read(env.session, LATEST_KEY) } : { first: null, latest: null };
      var u = parseUrl(env.location.href);
      var ref = env.document.referrer ? parseUrl(env.document.referrer) : null;
      return {
        v: VERSION,
        first: state.first,
        latest: state.latest,
        conversion: {
          url: u ? cleanUrl(u) : null,
          path: u ? clip(u.pathname, MAX) : null,
          title: clip(env.document.title, 200),
          referrer: ref && (ref.protocol === "http:" || ref.protocol === "https:") ? clip(ref.origin + ref.pathname, MAX) : null,
          at: new Date(env.now()).toISOString()
        }
      };
    }

    function forget() {
      remove(env.local, FIRST_KEY);
      remove(env.session, LATEST_KEY);
    }

    return { track: track, payload: payload, forget: forget };
  }

  /** A random id for one form submission (idempotency, not identity). */
  function submissionId(cryptoObj) {
    var bytes = new Uint8Array(16);
    if (cryptoObj && cryptoObj.getRandomValues) cryptoObj.getRandomValues(bytes);
    else for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    var out = "";
    for (var j = 0; j < bytes.length; j++) out += ("0" + bytes[j].toString(16)).slice(-2);
    return out;
  }

  function install(win) {
    // One id per page: a double click (two submit events) sends the same
    // id, so the Hub keeps one lead. Shopify's form then navigates away,
    // and the next page gets a new id.
    var pageSubmission = null;
    function pageSubmissionId() {
      if (!pageSubmission) pageSubmission = submissionId(win.crypto);
      return pageSubmission;
    }
    if (win.top !== win) return { payload: function () { return null; }, submissionId: pageSubmissionId };
    function storage(name) {
      try {
        return win[name];
      } catch (_e) {
        return null;
      }
    }
    var tracker = createTracker({
      location: win.location,
      document: win.document,
      local: storage("localStorage"),
      session: storage("sessionStorage"),
      now: function () {
        return Date.now();
      },
      consent: function () {
        try {
          var cp = win.Shopify && win.Shopify.customerPrivacy;
          if (cp && typeof cp.analyticsProcessingAllowed === "function") return !!cp.analyticsProcessingAllowed();
        } catch (_e) {
          /* not loaded yet */
        }
        return undefined;
      }
    });
    tracker.track();
    // The title may not be parsed yet when this runs in <head>.
    if (win.document.readyState === "loading") {
      win.document.addEventListener("DOMContentLoaded", tracker.track);
    }
    // Consent decided (or changed) after load: re-apply.
    win.document.addEventListener("visitorConsentCollected", tracker.track);
    return {
      payload: tracker.payload,
      forget: tracker.forget,
      submissionId: pageSubmissionId
    };
  }

  return { createTracker: createTracker, install: install, submissionId: submissionId, FIRST_KEY: FIRST_KEY, LATEST_KEY: LATEST_KEY, WINDOW_DAYS: WINDOW_DAYS };
});